import {
	Checkbox,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	Tabs,
	TextInput,
	Toolbar,
	ValidationError
} from "client/Components";
import { UsersAndUserGroupsDataModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	attachToUserAndUserGroupCollection
} from "client/Features/Users/UserAndUserGroupCollection/attachable";
import {
	UserAndUserGroupCollection,
	UserAndUserGroupKind
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupCollection";
import {
	ActiveTab
} from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	AlertManager,
	useAlerts,
	useDataInputDialog,
	useStableImperativeCallback
} from "client/Hooks";
import { EMPTY_ARRAY, isNil } from "client/Misc/utils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	doesNotExceedLength,
	hasMaxSize,
	isNotEmpty,
	isNotNullNorUndefined,
	isSupportedFileType
} from "client/Validation/rules";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { UserAndUserGroupIdsModel } from "client/Api/Workspace/workspace";
import styles from "./EditHubCategoryDialog.module.less";
import {
	add,
	HubItemCategoryInformationSaveModel,
	HubItemCategorySaveModel,
	HubItemCategorySaveResult,
	EditHubItemCategoryPageModel,
	EditHubItemCategoryPermissionModel,
	getCreateModel,
	getUpdateModel,
	HubItemKind,
	update,
	EditHubItemCategoryModel
} from "client/Api/Hub/hubItemCategories";
import { IValidationConfiguration } from "client/Validation/builder";
import { never } from "client_legacy/ndaw/errors";
import { InformationFormModel } from "./common";
import { KnowledgeItemForm } from "./KnowledgeItemForm";
import classNames from "classnames";
import { InvalidInputValidationResult } from "client/Validation/input";
import { tryParseServerErrors } from "client/Validation/serverErrors";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import { ModelPath } from "client/Validation/path";

export type EditHubCategoryDialogProps = {
	id: number | null;
	kind: HubItemKind;
	onSaved: (result: HubCategorySavedResult) => void;
	onClose: () => void;
};

export const EditHubCategoryDialog = (props: EditHubCategoryDialogProps): ReactElement => {
	const {
		id,
		kind,
		onSaved,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [imageInfo, setImageInfo] = useState<ImageInfo>(INITIAL_IMAGE_INFO);

	const [formData, setFormData] = useState<FormModel>(
		() => createDefaultSaveModel(kind)
	);

	const [usersAndUserGroupsData, setUsersAndUserGroupsData] = useState<UsersAndUserGroupsDataModel>({
		userGroups: [],
		users: []
	});

	const [canInformationBeChanged, setCanInformationBeChanged] = useState<boolean>(false);
	const [categoryInitialName, setCategoryInitialName] = useState<string | null>(null);

	const {
		imageId,
		maxImageSize,
		supportedImageTypes
	} = imageInfo;

	const alertManager: AlertManager = useAlerts();

	const validationConfiguration = useMemo(
		() => {
			const result: IValidationConfiguration<FormModel> = staticValidationConfiguration.clone();

			result
				.forObject("information")
				.whenType(x => x.isSet)
				.forObject("value")
				.whenType(x => x.kind === HubItemKind.KnowledgeItem)
				.forObject("knowledgeItemDisplayInfo")
				.forObject("image")
					.add(
						hasMaxSize(maxImageSize),
						isSupportedFileType(supportedImageTypes)
					);

			return result;
		},
		[maxImageSize, supportedImageTypes]
	);

	const formHandler: IFormHandler<FormModel> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const stableOnClose: () => void = useStableImperativeCallback(onClose);

	useEffect(
		(): (() => void) => {
			const load = async (id: number | null, signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					let pageModel: EditHubItemCategoryPageModel;
					if (isNil(id)) {
						pageModel = await getCreateModel(kind, signal);
					} else {
						pageModel = await getUpdateModel(id, signal);

						setCategoryInitialName(
							pageModel
								.category
								.name
						);
					}

					const {
						category,
						usersAndUserGroupsData
					} = pageModel;

					setUsersAndUserGroupsData(usersAndUserGroupsData);

					const {
						canInformationBeChanged,
						permissions
					} = category;

					setCanInformationBeChanged(canInformationBeChanged);

					setFormData({
						information: {
							// Despite the information not being always editable (canInformationBeChanged)
							// for the form infrastructure it is easier that the information is always there.
							// in the saveHandler we will unset it when it cannot be changed
							isSet: true,
							value: getInformation(category)
						},
						permissions
					});

					const imageInfo: ImageInfo | null = getImageInfo(category);
					setImageInfo(imageInfo ?? INITIAL_IMAGE_INFO);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();
			load(id, cancellation.signal);
			return () => cancellation.abort();
		},
		[
			alertManager,
			id,
			kind,
			stableOnClose
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validationResult: InputValidationResult = await formHandler.validation
				.validate();

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				setIsBusy(false);
				return;
			}

			try {
				const { model, name } = prepareModelForSaving(
					formData,
					canInformationBeChanged
				);

				let categoryId: number | null = id;
				if (isNil(categoryId)) {
					const result: HubItemCategorySaveResult = await add(kind, model);
					categoryId = result.categoryId;
				} else {
					await update(categoryId, model);
				}

				onSaved({
					id: categoryId,
					name
				});

				onClose();
			} catch (e) {
				console.error(e);

				const errorParseResult: TryResult<ReadonlyMap<ModelPath, InvalidInputValidationResult>> = tryParseServerErrors(
					e,
					[
						{
							pattern: new DotPathWildcardPattern("Information.Value.KnowledgeItemDisplayInfo.Image.**"),
							replacement: "Information.Value.KnowledgeItemDisplayInfo.Image"
						}
					]
				);

				if (errorParseResult.wasSuccessful) {
					const { result: errors } = errorParseResult;
					formHandler.validation
						.setServerErrors(errors);

					const imageError: string | undefined = errors
						.get(
							ModelPath.root
								.for("information")
								.for("value")
								.for("knowledgeItemDisplayInfo")
								.for("image")
						)
						?.errorMessage;

					if (imageError) {
						alertManager.showError(imageError);
					}
				} else {
					alertManager.showError(localize("_ChangesNotSaved"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			canInformationBeChanged,
			formData,
			formHandler.validation,
			id,
			kind,
			isBusy,
			onClose,
			onSaved
		]
	);

	const isExisting: boolean = id !== null;

	const permissionsAttachable: IValueInputAttachable<EditHubItemCategoryPermissionModel> = formHandler
		.for("permissions");

	return (
		<Dialog
			variant="left-slider"
			hasBusyIndicator={isBusy}
			trackingId="edit-hub-category-dialog"
			onClose={onClose}
		>
			<Dialog.Header
				title={isExisting
					? (
						<FormattedText
							text={localize("_EditNameCategory")}
							thingsToReplace={[
								{
									what: "{{Name}}",
									filler: categoryInitialName ?? ""
								}
							]}
						/>
					)
					: localize("_NewCategory")
				}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={saveHandler} height="fill">
					<Tabs<TabId> height="fill">
						<Tabs.Tab
							id={settingsTabId}
							title={localize("_Settings")}
						>
							<div
								className={classNames(
									styles.tab,
									styles.tab_widthConstrained
								)}
							>
								{formHandler.for("information")
									.whenType(
										(x): x is SetOptionalModel<InformationFormModel> => x.isSet,
										(x) => (
											<Settings
												attachable={x.for("value")}
												isEditable={canInformationBeChanged}
												imageId={imageId}
												supportedImageTypes={supportedImageTypes}
												maxImageSize={maxImageSize}
											/>
										)
								)}
							</div>
						</Tabs.Tab>

						<Tabs.Tab
							id={whoHasAccessTabId}
							title={localize("_WhoHasAccess")}
						>
							<div className={styles.tab}>
								<WhoHasAccess
									attachable={permissionsAttachable}
									usersAndUserGroupsData={usersAndUserGroupsData}
								/>
							</div>

						</Tabs.Tab>

						<Tabs.Tab
							id={editorsTabId}
							title={localize("_Editors")}
						>
							<div className={styles.tab}>
								<PermissionTable
									attachable={permissionsAttachable.for("editors")}
									usersAndUserGroupsData={usersAndUserGroupsData}
								/>
							</div>
						</Tabs.Tab>

						<Tabs.Tab
							id={ownersTabId}
							title={localize("_Owners")}
						>
							<div className={styles.tab}>
								<PermissionTable
									attachable={permissionsAttachable.for("owners")}
									usersAndUserGroupsData={usersAndUserGroupsData}
								/>
							</div>
						</Tabs.Tab>
					</Tabs>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize(
							isExisting
								? "_Save"
								: "_Create"
						)}
						onClick={saveHandler}
						trackingId="save"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						onClick={onClose}
						trackingId="cancel"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
EditHubCategoryDialog.displayName = "EditHubCategoryDialog";

type ImageInfo = {
	imageId: string | null;
	supportedImageTypes: readonly string[];
	maxImageSize: number;
};

const INITIAL_IMAGE_INFO: ImageInfo = {
	imageId: null,
	supportedImageTypes: EMPTY_ARRAY,
	maxImageSize: 0
};

export type SettingsProps = {
	attachable: IValueInputAttachable<InformationFormModel>;
	isEditable: boolean;
	imageId: string | null;
	supportedImageTypes: readonly string[];
	maxImageSize: number;
};

export const Settings = (props: SettingsProps): ReactElement => {
	const {
		attachable,
		isEditable,
		imageId,
		maxImageSize,
		supportedImageTypes
	} = props;

	return (
		<>
			<FormItem
				label={localize("_Name")}
				isRequired
			>
				<TextInput
					{...attachable.for("name")
						.attachToTextInput()
					}
					isDisabled={!isEditable}
				/>

				<ValidationError
					{...attachable.for("name")
						.attachToValidationError()
					}
				/>
			</FormItem>

			<FormItem
				label={localize("_Description")}
			>
				<TextInput
					{...attachable.for("description")
						.attachToTextInput()
					}
					isDisabled={!isEditable}
				/>

				<ValidationError
					{...attachable.for("description")
						.attachToValidationError()
					}
				/>
			</FormItem>

			{attachable.whenType(
				x => x.kind === HubItemKind.KnowledgeItem,
				knowledgeItemHandler => (
					<KnowledgeItemForm
						attachable={knowledgeItemHandler.for("knowledgeItemDisplayInfo")}
						imageId={imageId}
						maxImageSize={maxImageSize}
						supportedImageTypes={supportedImageTypes}
					/>
				)
			)}
		</>
	);
};
Settings.displayName = "Settings";

export type WhoHasAccessProps = {
	attachable: IValueInputAttachable<EditHubItemCategoryPermissionModel>;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

export const WhoHasAccess = (props: WhoHasAccessProps): ReactElement => {
	const {
		attachable,
		usersAndUserGroupsData
	} = props;

	return (
		<>
			<FormItem>
				<Checkbox
					{...attachable
						.for("everyoneHasAccess")
						.attachToCheckbox()
					}
					text={localize("_EveryoneHasAccess")}
				/>

				<ValidationError
					{...attachable
						.for("everyoneHasAccess")
						.attachToValidationError()
					}
				/>
			</FormItem>

			{!attachable.value.everyoneHasAccess && (
				<PermissionTable
					attachable={attachable.for("whoHasAccess")}
					usersAndUserGroupsData={usersAndUserGroupsData}
				/>
			)}
		</>
	);
};
WhoHasAccess.displayName = "WhoHasAccess";

export type PermissionTableProps = {
	attachable: IValueInputAttachable<UserAndUserGroupIdsModel>;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

export const PermissionTable = (props: PermissionTableProps): ReactElement => {
	const {
		attachable,
		usersAndUserGroupsData
	} = props;

	return (
		<FormItem height="stretch">
			<UserAndUserGroupCollection
				activeTab={ActiveTab.UserGroup}
				{...attachToUserAndUserGroupCollection(
					attachable,
					{
						kind: UserAndUserGroupKind.Edit,
						dialogTitleWhenAddingUsersAndUserGroups: localize("_AddGroupsOrUsers")
					}
				)}
				allUsersAndUserGroupsData={usersAndUserGroupsData}
			/>

			<ValidationError
				{...attachable.attachToValidationError()}
			/>
		</FormItem>
	);
};
PermissionTable.displayName = "PermissionTable";

type FormModel = Omit<HubItemCategorySaveModel, "information"> & {
	information: OptionalModel<InformationFormModel>;
};

type TabId =
	| typeof settingsTabId
	| typeof whoHasAccessTabId
	| typeof editorsTabId
	| typeof ownersTabId;

const settingsTabId = "settings";
const whoHasAccessTabId = "who-has-access";
const editorsTabId = "editors";
const ownersTabId = "owners";

const staticValidationConfiguration: IValidationConfiguration<FormModel> = createValidationConfiguration<FormModel>();

const informationValueConfiguration: IValidationConfiguration<InformationFormModel> = staticValidationConfiguration
	.forObject("information")
	.whenType(x => x.isSet)
	.forObject("value");

informationValueConfiguration.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

informationValueConfiguration.forScalar("description")
	.add(
		doesNotExceedLength(255)
	);

informationValueConfiguration
	.whenType(x => x.kind === HubItemKind.KnowledgeItem)
	.forObject("knowledgeItemDisplayInfo")
	.forObject("pageContent")
	.add(
		doesNotExceedLength(10_000)
	);

function createDefaultSaveModel(kind: HubItemKind): FormModel {
	return {
		information: getDefaultSaveInformation(kind),
		permissions: {
			everyoneHasAccess: true,
			whoHasAccess: {
				userIds: [],
				userGroupIds: []
			},
			editors: {
				userIds: [],
				userGroupIds: []
			},
			owners: {
				userIds: [],
				userGroupIds: []
			}
		}
	};
}

function getDefaultSaveInformation(
	kind: HubItemKind
): OptionalModel<InformationFormModel> {
	switch (kind) {
		case HubItemKind.Announcement:
		case HubItemKind.Event:
			return {
				isSet: true,
				value: {
					kind,
					name: "",
					description: ""
				}
			};
		case HubItemKind.KnowledgeItem:
			return {
				isSet: true,
				value: {
					kind: HubItemKind.KnowledgeItem,
					name: "",
					description: "",
					knowledgeItemDisplayInfo: {
						image: { isSet: false },
						pageContent: ""
					}
				}
			};
		default:
			never(kind);
	}
}

function getInformation(
	category: EditHubItemCategoryModel
): InformationFormModel {
	const {
		name,
		description
	} = category;

	switch(category.kind) {
		case HubItemKind.Announcement:
		case HubItemKind.Event:
			return {
				kind: category.kind,
				name,
				description
			};
		case HubItemKind.KnowledgeItem:
			return {
				kind: HubItemKind.KnowledgeItem,
				name,
				description,
				knowledgeItemDisplayInfo: {
					image: { isSet: false },
					pageContent: category.pageContent ?? ""
				}
			};
		default:
			never(category);
	}
}

function getImageInfo(
	category: EditHubItemCategoryModel
): ImageInfo | null {
	switch(category.kind) {
		case HubItemKind.Announcement:
		case HubItemKind.Event:
			return null;
		case HubItemKind.KnowledgeItem:
			const {
				imageId,
				maxImageSize,
				supportedImageTypes
			} = category;

			return {
				imageId,
				maxImageSize,
				supportedImageTypes
			};
		default:
			never(category);
	}
}

function prepareModelForSaving(
	formData: FormModel,
	canInformationBeChanged: boolean
): { model: HubItemCategorySaveModel; name: string; } {
	const {
		information,
		permissions
	} = formData;

	if (!information.isSet) {
		throw new Error("information is not set");
	}

	let informationSaveModel: HubItemCategoryInformationSaveModel;

	const {
		name,
		description
	} = information.value;

	switch(information.value.kind) {
		case HubItemKind.Announcement:
		case HubItemKind.Event:
			informationSaveModel = {
				name,
				description,
				knowledgeItemDisplayInfo: null
			};
			break;
		case HubItemKind.KnowledgeItem:
			const {
				knowledgeItemDisplayInfo
			} = information.value;

			informationSaveModel = {
				name,
				description,
				knowledgeItemDisplayInfo
			};
			break;
		default:
			never(information.value);
	}

	const model: HubItemCategorySaveModel = {
		information: canInformationBeChanged
			? {
				isSet: true,
				value: informationSaveModel
			}
			: { isSet: false },
		permissions: {
			...permissions,
			whoHasAccess: permissions.everyoneHasAccess
				? { userIds: [], userGroupIds: [] }
				: permissions.whoHasAccess
		}
	};

	return {
		model,
		name
	};
}

export type HubCategorySavedResult = {
	id: number;
	name: string;
};

export type EditHubCategoryOpenData = {
	kind: HubItemKind;
	id: number | null;
};

export function useEditHubCategoryDialog(
): [
	element: ReactElement | null,
	open: (openData: EditHubCategoryOpenData) => Promise<TryResult<HubCategorySavedResult>>
] {
	return useDataInputDialog<EditHubCategoryOpenData, HubCategorySavedResult>(
		(openData, onClose, onSaved) => (
			<EditHubCategoryDialog
				id={openData.id}
				kind={openData.kind}
				onClose={onClose}
				onSaved={onSaved}
			/>
		)
	);
}